import React, {
    useRef,
    useEffect,
    useMemo,
    useCallback,
    ForwardRefRenderFunction,
    RefObject,
    MutableRefObject,
    forwardRef,
    useImperativeHandle,
} from 'react';
import CN from 'classnames';
import { Scene, WebGLRenderer, PerspectiveCamera } from 'three';
import type { DesignSet } from '@app/react/const/type';
import { ASSETS_PATH } from '@app/react/const/variables';
import { createRenderer, createLight } from '@app/react/3d/utils/thumbnail';
import { useAnimationFrame } from '@app/react/3d/useAnimationFrame';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';

const setupThreejs = (
    container: RefObject<HTMLCanvasElement>,
    rendererRef: MutableRefObject<WebGLRenderer | null>,
    cameraRef: MutableRefObject<PerspectiveCamera | null>,
    scene: Scene,
    width: number,
    height: number,
) => {
    if (container.current === null) {
        return;
    }

    // 表示されていない componentはwidthがない
    // cssで適用しているデフォルト値とする
    const clientWidth = container.current?.clientWidth || width;
    const clientHeight = container.current?.clientHeight || height;
    console.log('[3d thumbnail] renderer size:', width, height);

    const webrender = createRenderer(
        container.current,
        clientWidth,
        clientHeight,
        window.devicePixelRatio,
    );
    rendererRef.current = webrender;

    const camera = new PerspectiveCamera(30, clientWidth / clientHeight, 0.01, 500);
    cameraRef.current = camera;
    cameraRef.current?.position.set(0, 0, 15);
    cameraRef.current?.updateMatrixWorld();

    rendererRef.current.render(scene, cameraRef.current);
};

export interface SceneHander {
    scene: () => Scene;
    rendererRef: () => MutableRefObject<WebGLRenderer | null>;
    cameraRef: () => MutableRefObject<PerspectiveCamera | null>;
}

export interface ArrowThumbnail3DProps {
    className?: string;
    checked?: boolean;
    item: DesignSet;
    availableDelete?: boolean;
    availableInCart?: boolean;
    hideCustomize?: boolean;
    onSelect?: (item: DesignSet) => void;
    onCustom?: (item: DesignSet) => void;
    onDelete?: (item: DesignSet) => void;
    onAddCart?: (item: DesignSet) => void;
    width?: number;
    height?: number;
}

type H = ForwardRefRenderFunction<SceneHander, ArrowThumbnail3DProps>;
export const ArrowThumbnail3D: H = (props, ref) => {
    const {
        className,
        checked = false,
        item,
        availableDelete = false,
        availableInCart = false,
        hideCustomize = false,
        onSelect,
        onDelete,
        onCustom,
        onAddCart,
        width = 335,
        height = 286,
    } = props;

    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();

    const container = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<WebGLRenderer | null>(null);
    const cameraRef = useRef<PerspectiveCamera | null>(null);

    const handleSelect = useCallback(() => {
        onSelect?.(item);
    }, [onSelect, item]);

    const handleDelete = useCallback(() => {
        onDelete?.(item);
    }, [onDelete, item]);

    const handleCustom = useCallback(() => {
        onCustom?.(item);
    }, [onCustom, item]);

    const handleAddCart = useCallback(() => {
        onAddCart?.(item);
    }, [onAddCart, item]);

    const rootClassName = CN('arrow-thumbnail-3d', className);
    const closeBtnClassName = CN('arrow-thumbnail-3d__close-btn', {
        hidden: !availableDelete,
    });
    const customizeBtnClassName = CN('arrow-thumbnail-3d__customize-btn', {
        hidden: !isMobile || hideCustomize,
    });
    const addCartBtnClassName = CN('arrow-thumbnail-3d__add-cart-btn', {
        hidden: !isMobile || !availableInCart,
    });

    const scene = useMemo(() => {
        const s = new Scene();

        const { hemisphereLight, ambientLight, directionalLights } = createLight();
        [hemisphereLight, ambientLight, ...directionalLights].forEach((l) => s.add(l));

        return s;
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            scene: () => scene,
            rendererRef: () => rendererRef,
            cameraRef: () => cameraRef,
        }),
        [scene, rendererRef, cameraRef],
    );

    useEffect(() => {
        setupThreejs(container, rendererRef, cameraRef, scene, width, height);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [container, scene, rendererRef, cameraRef]);

    const loop = useCallback(() => {
        if (cameraRef.current === null) {
            return;
        }
        rendererRef.current?.render(scene, cameraRef.current);
    }, [rendererRef, scene, cameraRef]);
    useAnimationFrame(loop, 1);

    return (
        <li className={rootClassName}>
            <button type="button" className={closeBtnClassName} onClick={handleDelete}>
                <img
                    src={`${ASSETS_PATH}/svg/icon-trash.svg`}
                    alt="close button"
                    className={`${closeBtnClassName}__img`}
                />
            </button>

            {isDesktop && checked && (
                <div className="arrow-thumbnail-3d__selectedIcon">選択中</div>
            )}

            <button type="button" className={customizeBtnClassName} id="fugafuga" onClick={handleCustom}>
                カスタマイズ
                <img
                    src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                    alt="go to customize page"
                    className={`${customizeBtnClassName}__img`}
                />
            </button>

            <button type="button" className={addCartBtnClassName} onClick={handleAddCart}>
                カートに追加
                <img
                    src={`${ASSETS_PATH}/svg/icon-oreder-cart.svg`}
                    alt="add cart button"
                    className={`${addCartBtnClassName}__img`}
                />
            </button>

            <canvas className="arrow-thumbnail-3d__body" ref={container} onClick={handleSelect} />
        </li>
    );
};

export const ArrowThumbnail3DRef = forwardRef<SceneHander, ArrowThumbnail3DProps>(ArrowThumbnail3D);
